import React, { useState } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import { useCourses } from '../../contexts/CoursesContext';
import AddCourse from './AddCourse';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import ViewCourse from './ViewCourse';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Search,
  Plus,
  BookOpen,
  Target,
  Clock,
  Trophy,
  Users,
  Edit,
  Trash2,
  Eye,
  MoreHorizontal
} from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const Courses: React.FC = () => {
  const { courses, isLoading } = useCourses();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [courseToDelete, setCourseToDelete] = useState<any>(null);
  const { deleteCourse } = useCourses();
  const { toast } = useToast();

  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;
    try {
      await deleteCourse(courseToDelete.id);
      toast({
        title: "Succès",
        description: "Le cours a été supprimé avec succès",
      });
      setCourseToDelete(null);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSemester = selectedSemester === 'all' || course.semester.includes(selectedSemester);
    return matchesSearch && matchesSemester;
  });

  // Calculate real stats from courses data
  const totalCourses = courses.length;
  const activeCourses = courses.filter(c => c.status === 'actif').length;
  const totalHours = courses.reduce((sum, c) => sum + c.hours, 0);
  const totalCredits = courses.reduce((sum, c) => sum + c.credits, 0);

  const stats = [
    {
      title: 'Total Cours',
      value: totalCourses.toString(),
      icon: BookOpen,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
      change: `${courses.length} cours`
    },
    {
      title: 'Cours Actifs',
      value: activeCourses.toString(),
      icon: Target,
      color: 'text-green-600',
      bg: 'bg-green-100 dark:bg-green-900/20',
      change: `${totalCourses > 0 ? Math.round((activeCourses / totalCourses) * 100) : 0}% du total`
    },
    {
      title: 'Heures Totales',
      value: totalHours.toLocaleString(),
      icon: Clock,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/20',
      change: 'Programme complet'
    },
    {
      title: 'Crédits Totaux',
      value: totalCredits.toString(),
      icon: Trophy,
      color: 'text-orange-600',
      bg: 'bg-orange-100 dark:bg-orange-900/20',
      change: 'Programme complet'
    }
  ];


  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'actif': return 'default';
      case 'inactif': return 'secondary';
      case 'archive': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusClassName = (status: string) => {
    switch (status) {
      case 'actif': return 'bg-green-100 text-green-800 hover:bg-green-200 border-none dark:bg-green-900/30 dark:text-green-300';
      case 'inactif': return 'bg-gray-100 text-gray-800 hover:bg-gray-200 border-none dark:bg-gray-900/30 dark:text-gray-300';
      case 'archive': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-none dark:bg-yellow-900/30 dark:text-yellow-300';
      default: return '';
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'actif': return 'Actif';
      case 'inactif': return 'Inactif';
      case 'archive': return 'Archivé';
      default: return status;
    }
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Chargement des cours...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Gestion des Cours
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez le programme académique de l'ISTMECH
            </p>
          </div>
          <Button onClick={() => {
            setSelectedCourse(null);
            setShowAddModal(true);
          }}>
            <Plus className="mr-2 h-4 w-4" /> Nouveau Cours
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6 flex items-center justify-between space-x-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold">{stat.value}</h3>
                    <span className="text-xs text-green-600 font-medium">
                      {stat.change}
                    </span>
                  </div>
                </div>
                <div className={`p-3 rounded-full ${stat.bg} ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters & Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle>Liste des cours</CardTitle>
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="relative w-full md:w-[300px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un cours..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <Tabs defaultValue="all" value={selectedSemester} onValueChange={setSelectedSemester} className="w-full md:w-auto">
                  <TabsList>
                    <TabsTrigger value="all">Tous</TabsTrigger>
                    <TabsTrigger value="1er semestre">Semestre 1</TabsTrigger>
                    <TabsTrigger value="2ème semestre">Semestre 2</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
            <CardDescription>
              Vue d'overview des cours, horaires et crédits.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cours</TableHead>
                    <TableHead>Semestre</TableHead>
                    <TableHead>Infos</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCourses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center">
                        Aucun cours trouvé.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCourses.map((course) => (
                      <TableRow key={course.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span className="font-semibold">{course.name}</span>
                            <span className="text-xs text-muted-foreground">{course.code} • {course.year}ème année</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {course.semester.map((sem, idx) => (
                            <span key={sem}>
                              {sem === '1' ? '1er' : '2ème'} sem
                              {idx < course.semester.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Trophy className="h-3 w-3" /> {course.credits} Crédits
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {course.hours}h
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" /> {course.currentStudents}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(course.status)} className={getStatusClassName(course.status)}>
                            {getStatusLabel(course.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                <span className="sr-only">Ouvrir menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  setShowViewModal(true);
                                }}
                              >
                                <Eye className="mr-2 h-4 w-4" /> Voir détails
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedCourse(course);
                                  setShowAddModal(true);
                                }}
                              >
                                <Edit className="mr-2 h-4 w-4" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="cursor-pointer text-red-600 focus:text-red-600"
                                onClick={() => setCourseToDelete(course)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="text-xs text-muted-foreground mt-4">
              Affichage de <strong>{filteredCourses.length}</strong> sur <strong>{courses.length}</strong> cours
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Course Modal */}
      <AddCourse
        open={showAddModal}
        onOpenChange={setShowAddModal}
        courseToEdit={selectedCourse}
      />

      {/* View Course Modal */}
      <ViewCourse
        open={showViewModal}
        onOpenChange={setShowViewModal}
        course={selectedCourse}
      />

      <AlertDialog open={!!courseToDelete} onOpenChange={(open: boolean) => !open && setCourseToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Cela supprimera définitivement le cours
              <strong> {courseToDelete?.name}</strong> et toutes les données associées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCourse}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Courses;
